import React, { useEffect, useMemo, useRef, useState } from 'react'
import { vn } from 'vend-number'
import { IPromise } from 'angular'
import {
  CashPaymentInformation,
  CashPaymentModal,
  RoundedNumber,
} from '../../../payments/cash-payment-modal/CashPaymentModal'
import { print } from '../../../print/print'
import {
  applyLspCashDiscountToOutstanding,
  roundCash,
} from '../../../../functions/payments'
import { useCompleteCashPayment } from '../hooks/useHandleCashPayment'
import { useFetchPaymentTypes } from '../hooks/useFetchPaymentTypes'
import { useSaleManager } from '../../../../ng-compat/hooks'
import {
  CashPaymentOption,
  isLspayCashDiscountingPaymentOption,
  LspayCashDiscountingPaymentOption,
  SalePayment,
} from './types'

interface Props {
  onClose: () => void
  onSuccess: (result: SalePayment) => void
  paymentOptions: CashPaymentOption | LspayCashDiscountingPaymentOption
  currencySymbol: string
  /**
   * When false, the underlying CashPaymentModal will never treat the
   * transaction as a partial payment. Used by the LSPay cash discounting flow
   * which must always settle the full balance.
   *
   * @default true
   */
  allowPartialPayment?: boolean
}

const buildLspDiscountedBalanceOwed = (
  lspayOption: LspayCashDiscountingPaymentOption
): RoundedNumber => {
  const rounded = roundCash(
    applyLspCashDiscountToOutstanding(
      vn(lspayOption.originalRoundedBalance.rounded).toString(),
      lspayOption.exclusivePrice,
      lspayOption.lspCashDiscountPercentage
    ),
    lspayOption.paymentType
  )

  return {
    rounded: vn(rounded.rounded.toString()),
    remainder: rounded.remainder,
  }
}

export const Cash = ({
  paymentOptions,
  onSuccess,
  onClose,
  currencySymbol,
  allowPartialPayment = true,
}: Props) => {
  const saleManager = useSaleManager()
  const lspayOption = isLspayCashDiscountingPaymentOption(paymentOptions)
    ? paymentOptions
    : null

  // When the customer dismisses the LSPay discount banner we revert to the
  // pre-discount amounts (and clear the discount on the sale) without
  // closing the modal.
  const [discountRemoved, setDiscountRemoved] = useState(false)
  const lspayDiscountActive = lspayOption !== null && !discountRemoved

  // Push the LSPay rate onto the sale so the outstanding balance, totals
  // and downstream summary panels reflect the discount. Clear it only when
  // the customer explicitly removes the banner – clearing on unmount would
  // wipe the discount immediately after a successful payment is recorded.
  // `newSale()` resets the rate between sales.
  useEffect(() => {
    if (!lspayOption) return
    const rate = discountRemoved ? 0 : lspayOption.lspCashDiscountPercentage
    // Failures here shouldn't block the cash modal; swallow rejections so we
    // don't surface an unhandled promise rejection.
    saleManager.setLspCashDiscountPercentage(rate).catch(() => undefined)
  }, [lspayOption, discountRemoved, saleManager])

  const { roundedAmountTendered, roundedBalance } =
    lspayOption && discountRemoved
      ? {
          roundedAmountTendered: lspayOption.originalRoundedAmountTendered,
          roundedBalance: lspayOption.originalRoundedBalance,
        }
      : paymentOptions

  const modalAmountToPay = useMemo(() => {
    if (!lspayDiscountActive || !lspayOption) {
      return roundedAmountTendered
    }

    return buildLspDiscountedBalanceOwed(lspayOption)
  }, [lspayDiscountActive, lspayOption, roundedAmountTendered])

  const modalBalanceRemainder = useMemo(() => {
    if (!lspayDiscountActive || !lspayOption) {
      return roundedBalance.remainder
    }

    return buildLspDiscountedBalanceOwed(lspayOption).remainder
  }, [lspayDiscountActive, lspayOption, roundedBalance.remainder])

  // Partial payments are blocked while the LSPay discount is applied; once
  // removed we fall back to the caller's allowPartialPayment preference.
  const isPartialPayment =
    !lspayDiscountActive &&
    allowPartialPayment &&
    vn(roundedAmountTendered.rounded).abs().lt(vn(roundedBalance.rounded).abs())

  const openedCashDrawer = useRef(false)
  const openCashDrawer = () => {
    if (openedCashDrawer.current) return
    openedCashDrawer.current = true
    print({ type: 'open-drawer' })
  }

  const { systemPaymentTypes } = useFetchPaymentTypes()
  const { completeRoundCashPayment } = useCompleteCashPayment(
    onSuccess,
    systemPaymentTypes.roundingPaymentType
  )

  const handleOnClose = (result: IPromise<CashPaymentInformation>) => {
    result
      .then((data: CashPaymentInformation) =>
        completeRoundCashPayment(data, paymentOptions.paymentType)
      )
      .catch(() => {
        onClose() // In original component, rejected promises just means the modal is closed
      })
  }

  return (
    <div className="payment-cash-modal">
      <CashPaymentModal
        amountToPay={modalAmountToPay}
        balanceRemainder={modalBalanceRemainder}
        close={handleOnClose}
        currencySymbol={currencySymbol}
        isPartialPayment={isPartialPayment}
        openCashDrawer={openCashDrawer}
        rounding={vn(paymentOptions.rounding)}
        cashDiscountRate={
          lspayDiscountActive
            ? lspayOption!.lspCashDiscountPercentage
            : undefined
        }
        onRemoveCashDiscount={
          lspayDiscountActive ? () => setDiscountRemoved(true) : undefined
        }
      />
    </div>
  )
}
